import { Tooltip } from 'antd';
import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  registerDriveFolder,
  saveGoogleToken,
  setGoogleAccessToken,
} from 'store/organisation/actions';
import { getGoogleAccessToken } from 'store/organisation/selectors';
import { GoogleConsts } from './google-consts';
import { useGoogleLibraries } from './google-library-hook';
import GoogleDriveIcon from 'shared/assets/images/google_drive.svg';
import { useTranslation } from 'react-i18next';
import IconButton from 'components/Custom/IconButton';

interface GoogleDrivePickerProps {
  connectionId: string;
  folderName: string | null;
  drivePath: string;
  syncExistingFiles: boolean;
  /** When true, UI is shown but OAuth/picker are inactive; tooltip includes "(Coming soon)". */
  disabled?: boolean;
  /** Render a custom trigger instead of the default icon button. Receives the picker's onClick handler. */
  renderTrigger?: (args: { onClick: () => void }) => React.ReactNode;
}

const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({
  connectionId,
  folderName,
  drivePath,
  syncExistingFiles,
  disabled = false,
  renderTrigger,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [connectClicked, setConnectClicked] = React.useState(false);

  const isGoogleReady = useGoogleLibraries(!disabled);
  const accessToken = useSelector(getGoogleAccessToken);

  useEffect(() => {
    if (disabled) return;
    dispatch(setGoogleAccessToken(null));
  }, [disabled, dispatch]);

  useEffect(() => {
    if (disabled) return;
    const script = document.createElement('script');
    script.src = GoogleConsts.API_URL;
    script.onload = () =>
      window.gapi.load('auth2', () => window.gapi.load('picker'));
    document.body.appendChild(script);
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;
    if (accessToken && isGoogleReady && connectClicked) {
      createPicker(accessToken);
    }
  }, [disabled, accessToken, isGoogleReady, connectClicked]);

  const handleAuth = () => {
    if (disabled) return;
    if (!isGoogleReady) return;
    setConnectClicked(true);

    const tokenClient = window.google.accounts.oauth2.initCodeClient({
      client_id: GoogleConsts.CLIENT_ID,
      scope: GoogleConsts.SCOPE,
      callback: (response) => {
        if (response.code) {
          dispatch(saveGoogleToken(response.code, connectionId));
        }
      },
    });
    tokenClient.requestCode();
  };

  const createPicker = (token) => {
    const view = new window.google.picker.DocsView(
      window.google.picker.ViewId.FOLDERS
    )
      .setSelectFolderEnabled(true)
      .setMimeTypes('application/vnd.google-apps.folder');

    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(token)
      .setDeveloperKey(GoogleConsts.DEVELOPER_KEY)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const folder = data.docs[0];
          dispatch(
            registerDriveFolder(
              folder.id,
              connectionId,
              null,
              'google',
              syncExistingFiles
            )
          );
        }
      })
      .build();
    picker.setVisible(true);
  };

  const baseLabel = t('organisation.receipts-connect-folder-google-drive');
  const labelForTooltip = disabled
    ? t('organisation.receipts-connect-folder-google-drive-coming-soon')
    : baseLabel;

  if (!renderTrigger) {
    const button = (
      <IconButton
        onClick={disabled ? () => {} : handleAuth}
        icon={GoogleDriveIcon}
        alt={baseLabel}
        tooltip={labelForTooltip}
        ariaLabel={labelForTooltip}
      />
    );
    if (disabled) {
      return (
        <span
          style={{
            display: 'inline-flex',
            opacity: 0.5,
            cursor: 'not-allowed',
          }}
        >
          {button}
        </span>
      );
    }
    return button;
  }

  const trigger = renderTrigger({
    onClick: disabled ? () => {} : handleAuth,
  });
  if (disabled) {
    return (
      <Tooltip title={labelForTooltip}>
        <span
          style={{
            display: 'inline-flex',
            opacity: 0.5,
            cursor: 'not-allowed',
          }}
        >
          {trigger}
        </span>
      </Tooltip>
    );
  }
  return <>{trigger}</>;
};

export default GoogleDrivePicker;
